import React from 'react';

const S = ({
    w = 'w-full',
    h = 'h-4',
    r = 'rounded-lg',
    cls = '',
    delay = '0s',
}: {
    w?: string;
    h?: string;
    r?: string;
    cls?: string;
    delay?: string;
}) => (
    <div
        className={`${w} ${h} ${r} ${cls} flex-shrink-0`}
        style={{
            background: 'linear-gradient(90deg,var(--v2-border-subtle) 25%,var(--v2-border-card) 50%,var(--v2-border-subtle) 75%)',
            backgroundSize: '200% 100%',
            animation: `skeletonShimmer 1.6s ease-in-out ${delay} infinite`,
        }}
    />
);

const OrderDetailSkeleton: React.FC = () => (
    <div className="flex flex-col min-h-screen v2-animate-fade-in">

        {/* ── HEADER — mirrors the sticky bar: back + avatar + name/badges/meta | stepper | actions ── */}
        <div className="bg-[var(--v2-bg-card)] border-b rounded-xl border-[var(--v2-border-card)] w-full">
            <div className="w-full p-4">
                <div className="flex items-center justify-between gap-3 py-2">
                    {/* Left: Back + Avatar + Name + Meta */}
                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                        <S w="w-7" h="h-7" r="rounded-lg" delay="0s" />
                        <S w="w-8" h="h-8" r="rounded-lg" delay="0.02s" />
                        <div className="flex flex-col min-w-0 gap-1.5">
                            <div className="flex items-center gap-1.5 flex-wrap">
                                <S w="w-28" h="h-[14px]" r="rounded-md" delay="0.04s" />
                                <S w="w-16" h="h-[14px]" r="rounded" delay="0.06s" />
                                <S w="w-[68px]" h="h-[16px]" r="rounded-full" delay="0.08s" />
                                <S w="w-[48px]" h="h-[16px]" r="rounded-full" delay="0.10s" />
                            </div>
                            <div className="flex items-center gap-1.5">
                                <S w="w-16" h="h-[12px]" r="rounded-md" delay="0.12s" />
                                <S w="w-24" h="h-[12px]" r="rounded-md" delay="0.14s" />
                                <S w="w-20" h="h-[12px]" r="rounded-md" delay="0.16s" />
                            </div>
                        </div>
                    </div>

                    {/* Center: Journey stepper */}
                    <div className="hidden lg:flex items-center flex-shrink-0">
                        {[0, 1, 2, 3].map((step) => (
                            <React.Fragment key={step}>
                                <div className="flex flex-col items-center gap-1.5 flex-shrink-0" style={{ width: 96 }}>
                                    <S w="w-7" h="h-7" r="rounded-full" delay={`${step * 0.05}s`} />
                                    <S w="w-14" h="h-2" r="rounded-md" delay={`${step * 0.05 + 0.02}s`} />
                                </div>
                                {step < 3 && (
                                    <div className="w-8 h-0.5 flex-shrink-0 -mt-4" style={{ background: 'var(--v2-border-subtle)' }} />
                                )}
                            </React.Fragment>
                        ))}
                    </div>

                    {/* Right: Action buttons */}
                    <div className="flex items-center gap-1.5 shrink-0">
                        <S w="w-[110px]" h="h-8" r="rounded-lg" delay="0.18s" />
                        <S w="w-20" h="h-8" r="rounded-lg" delay="0.20s" />
                        <S w="w-20" h="h-8" r="rounded-lg" delay="0.22s" />
                    </div>
                </div>
            </div>
        </div>

        {/* ── MAIN — workflow list: one card per test (specimen row + result row) ── */}
        <div className="w-full flex flex-col gap-4 pt-4">
            <div
                className="rounded-2xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] overflow-hidden"
                style={{ boxShadow: 'var(--v2-shadow-card)' }}
            >
                {/* Test header row */}
                <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[var(--v2-border-subtle)]">
                    <div className="flex items-center gap-2">
                        <S w="w-4" h="h-4" r="rounded-sm" delay="0s" />
                        <S w="w-44" h="h-[14px]" r="rounded-md" delay="0.02s" />
                    </div>
                    <S w="w-20" h="h-[18px]" r="rounded-full" delay="0.04s" />
                </div>

                {/* Specimen row */}
                <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[var(--v2-border-subtle)]">
                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                        <S w="w-7" h="h-7" r="rounded-lg" delay="0.06s" />
                        <div className="flex flex-col gap-1.5 flex-1 min-w-0">
                            <S w="w-32" h="h-[12px]" r="rounded-md" delay="0.08s" />
                            <S w="w-48" h="h-[10px]" r="rounded-md" delay="0.10s" />
                        </div>
                    </div>
                    <S w="w-28" h="h-8" r="rounded-lg" delay="0.12s" />
                </div>

                {/* Results row */}
                <div className="px-4 py-3 bg-[var(--v2-bg-surface)]">
                    <div className="flex items-center justify-between gap-3 mb-2.5">
                        <S w="w-16" h="h-[12px]" r="rounded-md" delay="0.14s" />
                        <S w="w-24" h="h-7" r="rounded-lg" delay="0.16s" />
                    </div>
                    <S w="w-full" h="h-12" r="rounded-lg" delay="0.18s" />
                </div>
            </div>
        </div>
    </div>
);

export default OrderDetailSkeleton;
